import { css, type Theme } from '@emotion/react';

import { Button, Modal } from '@toolbox/components';
import { CloseIcon } from '@toolbox/components/Icon/icons/CloseIcon';
import { useCloseWidget, useLeadData, useWidgetContext } from '@widget/core';
import { richTextResolver } from '@widget/storyblok/rteResolver';

import type { LeadData } from '@widget/core';
import type { Richtext } from 'storyblok-js-client';

const classes = {
  root: (theme: Theme) => css`
    background-color: white;
    width: 726px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 56px;
    position: relative;
    ${theme.media.down('sm')} {
      max-width: 360px;
      padding: 48px 32px 32px 32px;
    }
  `,
  title: (theme: Theme) => css`
    width: 100%;
    font-size: 32px;
    font-weight: ${theme.fontWeights.bold};
    color: ${theme.palette.colors.primary};
    ${theme.media.down('sm')} {
      font-size: ${theme.fontSizes.f7};
    }
  `,

  subtitle: (theme: Theme) => css`
    width: 100%;
    font-size: 18px;
    font-weight: ${theme.fontWeights.medium};
    color: #000000;
    margin-top: 32px;
    ${theme.media.down('sm')} {
      margin-top: 16px;
    }
  `,
  buttonContainer: (theme: Theme) => css`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 24px;
    margin-top: 48px;
    ${theme.media.down('sm')} {
      margin-top: 32px;
      flex-direction: column;
      row-gap: 24px;
    }
  `,
  button: (theme: Theme) => css`
    width: 100%;
    height: 56px;
    font-size: 18px !important;
    line-height: 0px !important;
    ${theme.media.down('md')} {
      height: 48px;
    }
    ${theme.media.down('sm')} {
      height: 40px;
    }
  `,
  closeIcon: css`
    position: absolute;
    top: 20px;
    right: 20px;
    cursor: pointer;
    width: 12px;
  `,
};

export type CloseModalProps = {
  title?: string | null;
  subtitle?: Richtext | string | null;
  closeModalText?: string | null;
  closeWidgetText?: string | null;
};

function getSubtitleHtml(subtitle: CloseModalProps['subtitle']) {
  if (!subtitle) {
    return '';
  }

  if (typeof subtitle === 'string') {
    return subtitle;
  }

  return richTextResolver.render(subtitle);
}

export const CloseConfirmationModal = ({
  title = 'Bewertungsprozess verlassen?',
  subtitle = 'Ihr Fortschritt wird nicht gespeichert.',
  closeModalText = 'Nein, auf der Seite bleiben',
  closeWidgetText = 'Ja, Seite verlassen',
}: CloseModalProps): JSX.Element => {
  const { setLeadData } = useLeadData();
  const { isOpenModal, setIsOpenModal } = useCloseWidget();
  const { close } = useWidgetContext();

  const handleCloseModal = (): void => {
    setLeadData({} as LeadData);
    close();
  };

  return (
    <Modal onRequestClose={() => setIsOpenModal(false)} isOpen={isOpenModal}>
      <div css={classes.root as any}>
        <CloseIcon onClick={() => setIsOpenModal(false)} css={classes.closeIcon as any} />
        <div css={classes.title as any}>{title}</div>
        <div
          css={classes.subtitle as any}
          dangerouslySetInnerHTML={{ __html: getSubtitleHtml(subtitle) }}
        />
        <div css={classes.buttonContainer as any}>
          <Button
            css={classes.button as any}
            onClick={() => setIsOpenModal(false)}
            variant="secondary"
          >
            {closeModalText}
          </Button>
          <Button onClick={handleCloseModal} css={classes.button as any}>
            {closeWidgetText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
